import React, { useState } from "react";

/**
 * Reusable TranslationsEditor.
 *
 * Props:
 *  - translations: { [lang]: { name?, description?, fields?: { [k]: v } } }
 *  - onChange(translations)
 *  - supported: list of language codes excluding default (e.g. ["ar","tr","ru"])
 *  - keys: list of plain string keys to edit per-language (e.g. ["name","description"])
 *  - fields: optional object of field-keys (entity custom fields) to edit per-language
 *  - testidPrefix: prefix for data-testid attributes
 */
export default function TranslationsEditor({ translations = {}, onChange, supported = ["ar", "tr", "ru"], keys = ["name", "description"], fields = null, testidPrefix = "tr" }) {
  const [selectedLang, setLang] = useState(supported[0] || "ar");
  if (!supported || supported.length === 0) return null;

  const lang = supported.includes(selectedLang) ? selectedLang : supported[0];
  const t = translations[lang] || {};
  const setT = (key, val) => onChange({ ...translations, [lang]: { ...t, [key]: val } });
  const setFieldT = (key, val) => onChange({ ...translations, [lang]: { ...t, fields: { ...(t.fields || {}), [key]: val } } });
  const isRtl = lang === "ar";

  return (
    <div className="mt-3 rounded-lg border border-zinc-200 p-3 bg-zinc-50/40">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-medium text-zinc-700">Translations</div>
        <div className="flex gap-1">
          {supported.map(l => (
            <button key={l} type="button" onClick={() => setLang(l)} data-testid={`${testidPrefix}-lang-${l}`}
              className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${lang === l ? "bg-zinc-900 text-white" : "border border-zinc-300 text-zinc-600"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        {keys.map((k) => (
          <div key={k} className="flex gap-2 items-start">
            <div className="text-[11px] text-zinc-500 w-24 shrink-0 mt-2">{k}</div>
            {(k === "description" || k.endsWith("_description")) ? (
              <textarea aria-label={`${k} (${lang})`} lang={lang} value={t[k] || ""} onChange={(e) => setT(k, e.target.value)} className="te flex-1 h-16" placeholder={`${k} in ${lang}`} dir={isRtl ? "rtl" : "ltr"} data-testid={`${testidPrefix}-${k}-${lang}`} />
            ) : (
              <input aria-label={`${k} (${lang})`} lang={lang} value={t[k] || ""} onChange={(e) => setT(k, e.target.value)} className="te flex-1" placeholder={`${k} in ${lang}`} dir={isRtl ? "rtl" : "ltr"} data-testid={`${testidPrefix}-${k}-${lang}`} />
            )}
          </div>
        ))}
        {fields && Object.keys(fields).map((k) => (
          <div key={k} className="flex gap-2 items-center">
            <div className="text-[11px] text-zinc-500 w-24 shrink-0 truncate">{k}</div>
            <input aria-label={`${k} (${lang})`} lang={lang} value={(t.fields || {})[k] || ""} onChange={(e) => setFieldT(k, e.target.value)} className="te flex-1" placeholder={`${k} in ${lang}`} dir={isRtl ? "rtl" : "ltr"} data-testid={`${testidPrefix}-field-${k}-${lang}`} />
          </div>
        ))}
      </div>
      <style>{`.te { padding: 0.5rem 0.75rem; border:1px solid #d4d4d8; border-radius:6px; background:#fff; outline:none; font-size: 0.875rem; }`}</style>
    </div>
  );
}
